/**
 * SynastryChartSVG — интерактивная SVG-визуализация синастрии
 *
 * Отображает две концентрические окружности:
 * - Карта A (внутренняя)
 * - Карта B (внешняя)
 * - Линии аспектов между картами с цветовой кодировкой
 * - Hover-эффекты с информацией об аспектах
 */

import React, { useState, useMemo } from 'react';

// ============================================================
// Типы
// ============================================================

interface PlanetData {
  name: string;
  nameRu: string;
  symbol: string;
  sign: string;
  signRu: string;
  longitude: number;
  house: number;
  retrograde: boolean;
}

interface SynastryAspectData {
  planetA: string;
  planetARu: string;
  planetB: string;
  planetBRu: string;
  type: string;
  orb: number;
  strength: number;
  longitudeA: number;
  longitudeB: number;
}

interface HouseData {
  number: number;
  sign: string;
  signRu: string;
  longitude: number;
}

interface SynastryChartSVGProps {
  /** Планеты человека A (внутренний круг) */
  planetsA: PlanetData[];
  /** Планеты человека B (внешний круг) */
  planetsB: PlanetData[];
  /** Дома человека A */
  housesA: HouseData[];
  /** Дома человека B */
  housesB: HouseData[];
  /** Синастрические аспекты */
  aspects: SynastryAspectData[];
  /** Долгота Асцендента A */
  ascendantLongitudeA?: number;
  /** Долгота Асцендента B */
  ascendantLongitudeB?: number;
  /** Размер SVG */
  size?: number;
  /** Имя человека A */
  nameA?: string;
  /** Имя человека B */
  nameB?: string;
  /** Показать композитную карту (третий круг) */
  showComposite?: boolean;
  /** Планеты композитной карты (средние точки) */
  planetsComposite?: PlanetData[];
  /** Дома композитной карты */
  housesComposite?: HouseData[];
  /** Долгота Асцендента композита */
  ascendantLongitudeComposite?: number;
}

// ============================================================
// Константы
// ============================================================

const ZODIAC_SYMBOLS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
const ZODIAC_COLORS = [
  '#FF4444', '#44AA44', '#FFAA00', '#4488CC',
  '#FF6600', '#668844', '#FF88AA', '#882222',
  '#AA6600', '#444444', '#4466CC', '#66AAAA',
];

/** Цвета аспектов */
const ASPECT_COLORS: Record<string, string> = {
  'conjunction': '#FFD700',
  'opposition': '#FF4444',
  'trine': '#44BB44',
  'square': '#FF6600',
  'sextile': '#4488FF',
  'quincunx': '#AA44FF',
  'semi-sextile': '#88AACC',
  'semi-square': '#CC8844',
  'sesquiquadrate': '#CC6688',
  'quintile': '#44CCAA',
  'bi-quintile': '#66CCAA',
  // Русские названия
  'Соединение': '#FFD700',
  'Оппозиция': '#FF4444',
  'Трин': '#44BB44',
  'Квадрат': '#FF6600',
  'Секстиль': '#4488FF',
  'Квинконс': '#AA44FF',
};

/** Радиусы колец (viewBox 0-700) */
const RADIUS = {
  outerB: 330,       // внешний край знаков B
  signInnerB: 300,   // внутренний край знаков B
  houseOuterB: 300,  // внешний край домов B
  houseInnerB: 260,  // внутренний край домов B
  gap: 250,          // промежуток между картами (для аспектов)
  houseOuterA: 240,  // внешний край домов A
  houseInnerA: 200,  // внутренний край домов A
  signOuterA: 200,   // внешний край знаков A
  signInnerA: 175,   // внутренний край знаков A
  planetA: 155,      // планеты A
  planetB: 280,      // планеты B
  inner: 140,        // внутренний круг
  // Composite ring (Ring C) — между A и B
  signOuterC: 210,   // внешний край знаков C
  signInnerC: 185,   // внутренний край знаков C
  houseOuterC: 210,  // внешний край домов C
  houseInnerC: 185,  // внутренний край домов C
  planetC: 197,      // планеты C (composite)
};

// ============================================================
// Компонент
// ============================================================

const SynastryChartSVG: React.FC<SynastryChartSVGProps> = ({
  planetsA,
  planetsB,
  housesA,
  housesB,
  aspects,
  ascendantLongitudeA = 0,
  ascendantLongitudeB = 0,
  size = 700,
  nameA = 'A',
  nameB = 'B',
  showComposite = false,
  planetsComposite = [],
  housesComposite = [],
  ascendantLongitudeComposite = 0,
}) => {
  const [hoveredAspect, setHoveredAspect] = useState<number | null>(null);
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null);

  const cx = 350;
  const cy = 350;

  /** Конвертация долготы в SVG-угол */
  const lonToAngleA = (longitude: number): number => {
    return (ascendantLongitudeA - longitude + 180) * (Math.PI / 180);
  };

  const lonToAngleB = (longitude: number): number => {
    return (ascendantLongitudeB - longitude + 180) * (Math.PI / 180);
  };

  const lonToAngleC = (longitude: number): number => {
    return (ascendantLongitudeComposite - longitude + 180) * (Math.PI / 180);
  };

  /** Координаты точки на круге */
  const pointOnCircle = (
    angle: number,
    radius: number
  ): { x: number; y: number } => ({
    x: cx + radius * Math.cos(angle),
    y: cy - radius * Math.sin(angle),
  });

  /** Позиции планет с предотвращением наложений */
  const calcPlanetPositions = (
    planets: PlanetData[],
    lonToAngle: (lon: number) => number,
    radius: number
  ): Map<string, { x: number; y: number }> => {
    const sorted = [...planets].sort((a, b) => a.longitude - b.longitude);
    const positions = new Map<string, { x: number; y: number }>();
    const minGap = 6 * (Math.PI / 180);

    let lastAngle = -Infinity;

    sorted.forEach((planet) => {
      let angle = lonToAngle(planet.longitude);

      if (lastAngle !== -Infinity) {
        let diff = lastAngle - angle;
        if (diff < 0) diff += 2 * Math.PI;
        if (diff < minGap) {
          angle = lastAngle - minGap;
        }
      }

      const pos = pointOnCircle(angle, radius);
      positions.set(planet.name, pos);
      lastAngle = angle;
    });

    return positions;
  };

  const positionsA = useMemo(
    () => calcPlanetPositions(planetsA, lonToAngleA, RADIUS.planetA),
    [planetsA, ascendantLongitudeA]
  );

  const positionsB = useMemo(
    () => calcPlanetPositions(planetsB, lonToAngleB, RADIUS.planetB),
    [planetsB, ascendantLongitudeB]
  );

  const positionsC = useMemo(
    () => calcPlanetPositions(planetsComposite, lonToAngleC, RADIUS.planetC),
    [planetsComposite, ascendantLongitudeComposite]
  );

  /** Генерация SVG-дуги для знака зодиака */
  const signArcPath = (
    startLon: number,
    endLon: number,
    outerR: number,
    innerR: number,
    lonToAngle: (lon: number) => number
  ): string => {
    const startAngle = lonToAngle(startLon);
    const endAngle = lonToAngle(endLon);

    const outerStart = pointOnCircle(startAngle, outerR);
    const outerEnd = pointOnCircle(endAngle, outerR);
    const innerStart = pointOnCircle(startAngle, innerR);
    const innerEnd = pointOnCircle(endAngle, innerR);

    const largeArc = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;

    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerEnd.x} ${innerEnd.y}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
      'Z',
    ].join(' ');
  };

  /** Позиция текста знака */
  const signTextPos = (
    startLon: number,
    endLon: number,
    radius: number,
    lonToAngle: (lon: number) => number
  ): { x: number; y: number; rotation: number } => {
    const midLon = (startLon + endLon) / 2;
    const angle = lonToAngle(midLon);
    const pos = pointOnCircle(angle, radius);
    const rotation = -angle * (180 / Math.PI);
    return { x: pos.x, y: pos.y, rotation };
  };

  return (
    <svg
      viewBox="0 0 700 700"
      width={size}
      height={size}
      style={{ maxWidth: '100%', height: 'auto' }}
    >
      {/* Фон */}
      <defs>
        <radialGradient id="synastryBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a1a2e" />
          <stop offset="100%" stopColor="#0a0a15" />
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect width="700" height="700" fill="url(#synastryBg)" rx="8" />

      {/* ===== КАРТА B (внешняя) ===== */}

      {/* Знаки зодиака B */}
      {housesB.map((house, i) => {
        const startLon = house.longitude;
        const nextHouse = housesB[(i + 1) % 12];
        const endLon = nextHouse.longitude;
        const signIndex = i;

        return (
          <g key={`signB-${i}`}>
            <path
              d={signArcPath(startLon, endLon, RADIUS.outerB, RADIUS.signInnerB, lonToAngleB)}
              fill={ZODIAC_COLORS[signIndex]}
              fillOpacity={0.15}
              stroke="#444"
              strokeWidth={0.5}
            />
            {(() => {
              const tp = signTextPos(startLon, endLon, (RADIUS.outerB + RADIUS.signInnerB) / 2, lonToAngleB);
              return (
                <text
                  x={tp.x}
                  y={tp.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="12"
                  fill={ZODIAC_COLORS[signIndex]}
                  transform={`rotate(${tp.rotation}, ${tp.x}, ${tp.y})`}
                >
                  {ZODIAC_SYMBOLS[signIndex]}
                </text>
              );
            })()}
          </g>
        );
      })}

      {/* Дома B — линии */}
      {housesB.map((house, i) => {
        const angle = lonToAngleB(house.longitude);
        const inner = pointOnCircle(angle, RADIUS.houseInnerB);
        const outer = pointOnCircle(angle, RADIUS.outerB);
        return (
          <line
            key={`houseB-line-${i}`}
            x1={inner.x} y1={inner.y}
            x2={outer.x} y2={outer.y}
            stroke="#555"
            strokeWidth={house.number === 1 || house.number === 10 ? 1.5 : 0.5}
          />
        );
      })}

      {/* Кольца B */}
      <circle cx={cx} cy={cy} r={RADIUS.outerB} fill="none" stroke="#555" strokeWidth={1} />
      <circle cx={cx} cy={cy} r={RADIUS.signInnerB} fill="none" stroke="#555" strokeWidth={0.5} />
      <circle cx={cx} cy={cy} r={RADIUS.houseInnerB} fill="none" stroke="#444" strokeWidth={0.5} />

      {/* ===== КАРТА A (внутренняя) ===== */}

      {/* Знаки зодиака A */}
      {housesA.map((house, i) => {
        const startLon = house.longitude;
        const nextHouse = housesA[(i + 1) % 12];
        const endLon = nextHouse.longitude;
        const signIndex = i;

        return (
          <g key={`signA-${i}`}>
            <path
              d={signArcPath(startLon, endLon, RADIUS.signOuterA, RADIUS.signInnerA, lonToAngleA)}
              fill={ZODIAC_COLORS[signIndex]}
              fillOpacity={0.2}
              stroke="#555"
              strokeWidth={0.5}
            />
            {(() => {
              const tp = signTextPos(startLon, endLon, (RADIUS.signOuterA + RADIUS.signInnerA) / 2, lonToAngleA);
              return (
                <text
                  x={tp.x}
                  y={tp.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="11"
                  fill={ZODIAC_COLORS[signIndex]}
                  transform={`rotate(${tp.rotation}, ${tp.x}, ${tp.y})`}
                >
                  {ZODIAC_SYMBOLS[signIndex]}
                </text>
              );
            })()}
          </g>
        );
      })}

      {/* Дома A — линии */}
      {housesA.map((house, i) => {
        const angle = lonToAngleA(house.longitude);
        const inner = pointOnCircle(angle, RADIUS.inner);
        const outer = pointOnCircle(angle, RADIUS.signOuterA);
        return (
          <line
            key={`houseA-line-${i}`}
            x1={inner.x} y1={inner.y}
            x2={outer.x} y2={outer.y}
            stroke="#666"
            strokeWidth={house.number === 1 || house.number === 10 ? 1.5 : 0.5}
          />
        );
      })}

      {/* Кольца A */}
      <circle cx={cx} cy={cy} r={RADIUS.signOuterA} fill="none" stroke="#666" strokeWidth={1} />
      <circle cx={cx} cy={cy} r={RADIUS.signInnerA} fill="none" stroke="#555" strokeWidth={0.5} />
      <circle cx={cx} cy={cy} r={RADIUS.inner} fill="none" stroke="#444" strokeWidth={0.5} />

      {/* ===== КАРТА C (комозитная — между A и B) ===== */}
      {showComposite && housesComposite.length > 0 && (
        <>
          {/* Знаки зодиака C */}
          {housesComposite.map((house, i) => {
            const startLon = house.longitude;
            const nextHouse = housesComposite[(i + 1) % 12];
            const endLon = nextHouse.longitude;
            const signIndex = i;

            return (
              <g key={`signC-${i}`}>
                <path
                  d={signArcPath(startLon, endLon, RADIUS.signOuterC, RADIUS.signInnerC, lonToAngleC)}
                  fill={ZODIAC_COLORS[signIndex]}
                  fillOpacity={0.12}
                  stroke="#888"
                  strokeWidth={0.5}
                />
                {(() => {
                  const tp = signTextPos(startLon, endLon, (RADIUS.signOuterC + RADIUS.signInnerC) / 2, lonToAngleC);
                  return (
                    <text
                      x={tp.x}
                      y={tp.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="10"
                      fill={ZODIAC_COLORS[signIndex]}
                      transform={`rotate(${tp.rotation}, ${tp.x}, ${tp.y})`}
                    >
                      {ZODIAC_SYMBOLS[signIndex]}
                    </text>
                  );
                })()}
              </g>
            );
          })}

          {/* Дома C — линии */}
          {housesComposite.map((house, i) => {
            const angle = lonToAngleC(house.longitude);
            const inner = pointOnCircle(angle, RADIUS.houseInnerC);
            const outer = pointOnCircle(angle, RADIUS.houseOuterC);
            return (
              <line
                key={`houseC-line-${i}`}
                x1={inner.x} y1={inner.y}
                x2={outer.x} y2={outer.y}
                stroke="#777"
                strokeWidth={house.number === 1 || house.number === 10 ? 1.5 : 0.5}
              />
            );
          })}

          {/* Кольца C */}
          <circle cx={cx} cy={cy} r={RADIUS.signOuterC} fill="none" stroke="#777" strokeWidth={0.8} />
          <circle cx={cx} cy={cy} r={RADIUS.signInnerC} fill="none" stroke="#666" strokeWidth={0.5} />

          {/* Планеты C (композитные) */}
          {planetsComposite.map((planet) => {
            const pos = positionsC.get(planet.name);
            if (!pos) return null;
            const isHovered = hoveredPlanet === `C-${planet.name}`;

            return (
              <g
                key={`planetC-${planet.name}`}
                onMouseEnter={() => setHoveredPlanet(`C-${planet.name}`)}
                onMouseLeave={() => setHoveredPlanet(null)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={isHovered ? 11 : 9}
                  fill="#1a1a2e"
                  fillOpacity={0.8}
                  stroke="#AA88CC"
                  strokeWidth={isHovered ? 2 : 1}
                />
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={isHovered ? '11' : '9'}
                  fill="#AA88CC"
                  filter={isHovered ? 'url(#glow)' : undefined}
                >
                  {planet.symbol}
                </text>
              </g>
            );
          })}
        </>
      )}

      {/* ===== АСПЕКТЫ МЕЖДУ КАРТАМИ ===== */}
      {aspects.map((aspect, i) => {
        const posA = positionsA.get(aspect.planetA);
        const posB = positionsB.get(aspect.planetB);
        if (!posA || !posB) return null;

        const color = ASPECT_COLORS[aspect.type] || '#888';
        const isHovered = hoveredAspect === i;
        const opacity = isHovered ? 0.9 : 0.4;
        const strokeWidth = isHovered ? 2 : 1;

        return (
          <g key={`aspect-${i}`}>
            <line
              x1={posA.x} y1={posA.y}
              x2={posB.x} y2={posB.y}
              stroke={color}
              strokeWidth={strokeWidth}
              strokeOpacity={opacity}
              strokeDasharray={aspect.type === 'square' || aspect.type === 'Квадрат' ? '4,2' : undefined}
              onMouseEnter={() => setHoveredAspect(i)}
              onMouseLeave={() => setHoveredAspect(null)}
              style={{ cursor: 'pointer' }}
            />
            {/* Невидимая широкая линия для hover */}
            <line
              x1={posA.x} y1={posA.y}
              x2={posB.x} y2={posB.y}
              stroke="transparent"
              strokeWidth={8}
              onMouseEnter={() => setHoveredAspect(i)}
              onMouseLeave={() => setHoveredAspect(null)}
              style={{ cursor: 'pointer' }}
            />
          </g>
        );
      })}

      {/* ===== ПЛАНЕТЫ A (внутренний круг) ===== */}
      {planetsA.map((planet) => {
        const pos = positionsA.get(planet.name);
        if (!pos) return null;
        const isHovered = hoveredPlanet === `A-${planet.name}`;

        return (
          <g
            key={`planetA-${planet.name}`}
            onMouseEnter={() => setHoveredPlanet(`A-${planet.name}`)}
            onMouseLeave={() => setHoveredPlanet(null)}
            style={{ cursor: 'pointer' }}
          >
            <circle
              cx={pos.x}
              cy={pos.y}
              r={isHovered ? 12 : 10}
              fill="#1a1a2e"
              fillOpacity={0.8}
              stroke="#6699CC"
              strokeWidth={isHovered ? 2 : 1}
            />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={isHovered ? '12' : '10'}
              fill="#6699CC"
              filter={isHovered ? 'url(#glow)' : undefined}
            >
              {planet.symbol}
            </text>
          </g>
        );
      })}

      {/* ===== ПЛАНЕТЫ B (внешний круг) ===== */}
      {planetsB.map((planet) => {
        const pos = positionsB.get(planet.name);
        if (!pos) return null;
        const isHovered = hoveredPlanet === `B-${planet.name}`;

        return (
          <g
            key={`planetB-${planet.name}`}
            onMouseEnter={() => setHoveredPlanet(`B-${planet.name}`)}
            onMouseLeave={() => setHoveredPlanet(null)}
            style={{ cursor: 'pointer' }}
          >
            <circle
              cx={pos.x}
              cy={pos.y}
              r={isHovered ? 12 : 10}
              fill="#1a1a2e"
              fillOpacity={0.8}
              stroke="#CC9966"
              strokeWidth={isHovered ? 2 : 1}
            />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={isHovered ? '12' : '10'}
              fill="#CC9966"
              filter={isHovered ? 'url(#glow)' : undefined}
            >
              {planet.symbol}
            </text>
          </g>
        );
      })}

      {/* ===== ЦЕНТР: ИНИЦИАЛЫ ===== */}
      <circle cx={cx} cy={cy} r={30} fill="#1a1a2e" stroke="#555" strokeWidth={1} />
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="11" fill="#6699CC" fontWeight="bold">
        {nameA.charAt(0).toUpperCase()}
      </text>
      <line x1={cx - 15} y1={cy} x2={cx + 15} y2={cy} stroke="#555" strokeWidth={0.5} />
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#CC9966" fontWeight="bold">
        {nameB.charAt(0).toUpperCase()}
      </text>

      {/* ===== TOOLTIP ===== */}
      {hoveredAspect !== null && aspects[hoveredAspect] && (() => {
        const aspect = aspects[hoveredAspect];
        const posA = positionsA.get(aspect.planetA);
        const posB = positionsB.get(aspect.planetB);
        if (!posA || !posB) return null;

        const tooltipX = (posA.x + posB.x) / 2;
        const tooltipY = (posA.y + posB.y) / 2;
        const color = ASPECT_COLORS[aspect.type] || '#888';

        return (
          <g>
            <rect
              x={tooltipX - 80}
              y={tooltipY - 25}
              width={160}
              height={40}
              rx={4}
              fill="#1a1a2e"
              fillOpacity={0.95}
              stroke={color}
              strokeWidth={1}
            />
            <text x={tooltipX} y={tooltipY - 8} textAnchor="middle" fontSize="10" fill="#fff">
              {aspect.planetARu} {aspect.type} {aspect.planetBRu}
            </text>
            <text x={tooltipX} y={tooltipY + 8} textAnchor="middle" fontSize="9" fill="#aaa">
              Орбис: {aspect.orb}° | Сила: {Math.round(aspect.strength * 100)}%
            </text>
          </g>
        );
      })()}

      {/* ===== ЛЕГЕНДА ===== */}
      <g transform="translate(10, 660)">
        <text fontSize="9" fill="#6699CC">● {nameA}</text>
        <text x={80} fontSize="9" fill="#CC9966">● {nameB}</text>
      </g>
    </svg>
  );
};

export default SynastryChartSVG;
